import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOpsService } from '../../../shared/api/opsService';
import { OVERALL_ORDER } from '../components/healthUtils';
import { kernelKeys } from './queryKeys';
import type { ReconcilerHealth } from '../../../types/backend';

function sortWorstFirst(reconcilers: ReconcilerHealth[]): ReconcilerHealth[] {
  return [...reconcilers].sort((a, b) => {
    const rank = OVERALL_ORDER[a.overall] - OVERALL_ORDER[b.overall];
    return rank !== 0 ? rank : a.domain.localeCompare(b.domain);
  });
}

export interface ControlPlaneHealthState {
  reconcilers: ReconcilerHealth[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useControlPlaneHealth(): ControlPlaneHealthState {
  const ops = useOpsService();

  // Manual fetch only: the Kernel modal is always mounted, so it drives the
  // fetch via refresh() when opened rather than auto-fetching on mount.
  const query = useQuery({
    queryKey: kernelKeys.controlPlaneHealth(),
    queryFn: async () => sortWorstFirst(await ops.getControlPlaneHealth()),
    enabled: false,
  });

  const { refetch } = query;
  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    reconcilers: (query.data ?? []) as ReconcilerHealth[],
    loading: query.isFetching,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to load control-plane health'
      : null,
    refresh,
  };
}
